import { inject, Injectable, signal } from '@angular/core';
import { Observable, of } from 'rxjs';

import { ApiService } from '../../../services/api.service';
import {
  CountUsersResponse,
  CreateCallResponse,
  CreateLinkMeetingResponse,
  CreateMeetingResponse,
  GetMeetingInfoResponse,
  JoinMeetingResponse,
} from '../types/meeting-management';

@Injectable({
  providedIn: 'root',
})
export class MeetingManagementService {
  private apiService = inject(ApiService);
  canStartJoin = signal('');

  createMeeting = (body: {
    meetingName: string;
    meetingId: string;
    adminIds: string[];
    userIds: string[];
    groupIds: string[];
    hostInfo: { name: string; userId: string };
    groupId?: string;
  }) => {
    return this.apiService.post<CreateMeetingResponse>(
      '/meeting/zego-cloud-meeting/create-meeting',
      body,
      {},
    );
  };

  createLinkMeeting = (isPublic: number) => {
    return this.apiService.post<CreateLinkMeetingResponse>(
      '/meeting/zego-cloud-meeting/create-link-meeting',
      { isPublic, isWeb: true },
      {},
    );
  };

  joinMeeting = (userId: string, meetingId: string) => {
    return this.apiService.post<JoinMeetingResponse>(
      '/meeting/zego-cloud-meeting/join-meeting',
      { userId, meetingId },
      {},
    );
  };

  getMeetingInfo = (meetingId: string) => {
    return this.apiService.post<GetMeetingInfoResponse>(
      '/meeting/zego-cloud-meeting/get-meeting-info',
      { meetingId },
      {},
    );
  };

  private prefetchedMeetingInfo: {
    meetingId: string;
    res: GetMeetingInfoResponse;
    at: number;
  } | null = null;

  private static readonly PREFETCH_TTL_MS = 15000;

  setPrefetchedMeetingInfo(meetingId: string, res: GetMeetingInfoResponse) {
    this.prefetchedMeetingInfo = { meetingId, res, at: Date.now() };
  }

  private takePrefetchedMeetingInfo(meetingId: string): GetMeetingInfoResponse | null {
    const prefetched = this.prefetchedMeetingInfo;
    this.prefetchedMeetingInfo = null;

    if (!prefetched || prefetched.meetingId !== meetingId) {
      return null;
    }
    if (Date.now() - prefetched.at > MeetingManagementService.PREFETCH_TTL_MS) {
      return null;
    }
    return prefetched.res;
  }

  getMeetingInfoOnce = (meetingId: string): Observable<GetMeetingInfoResponse> => {
    const prefetched = this.takePrefetchedMeetingInfo(meetingId);
    return prefetched ? of(prefetched) : this.getMeetingInfo(meetingId);
  };

  addAdmin = (meetingId: string, targetUserId: string) => {
    return this.apiService.post<GetMeetingInfoResponse>(
      '/meeting/zego-cloud-meeting/add-admin',
      { meetingId, targetUserId },
      {},
    );
  };

  removeAdmin = (meetingId: string, targetUserId: string) => {
    return this.apiService.post<GetMeetingInfoResponse>(
      '/meeting/zego-cloud-meeting/remove-admin',
      { meetingId, targetUserId },
      {},
    );
  };

  createCall = (body: {
    isVideo: boolean;
    roomId: string;
    calleeId: string[];
    callerId: string;
  }) => {
    return this.apiService.post<CreateCallResponse>(
      '/meeting/zego-call-bubble/create-call',
      body,
      {},
    );
  };

  countUsersByRoomId = ({ roomId }: { roomId: string }) => {
    return this.apiService.get<CountUsersResponse>(
      `/meeting/zego-cloud-meeting/count-user/${roomId}`,
    );
  };
}
